/**
 * The assistant drawer's opening view — the product's differentiator: it
 * leads with the couple's actual planning status, not an empty chat box.
 *
 *   a warm lead line → recommended next steps (rule-based insights, no LLM)
 *   → dynamic suggestion chips derived from the same event state.
 *
 * Insights come from GET /assistant/insights as rule/chip IDS + params; all
 * copy is localized here (assistant.insights.* / assistant.chips.*), so it
 * is deterministic, free, and fully bilingual. Degrades gracefully: if the
 * fetch fails, a generic chip set renders and chat is unaffected.
 *
 * Kept deliberately calm: the countdown that used to headline this view was
 * dropped (the drawer header already frames the event), and the next-steps
 * list is capped to the few most urgent items rendered as spacious, soft
 * rows — so the panel reads as a gentle briefing, not a dense dashboard.
 */

import { Box, Chip, Skeleton, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import IconifyIcon from 'components/base/IconifyIcon';
import { useAssistantInsights } from './useAssistantInsights';

// The list is priority-sorted (BE); showing only the top few keeps the opening
// view uncluttered — the rest surface through chat and the suggestion chips.
const MAX_VISIBLE_INSIGHTS = 3;

interface AssistantHomeProps {
  eventId: string;
  /** Sending a chip prefills + sends it as a message. */
  onPrompt: (content: string) => void;
  disabled: boolean;
}

const AssistantHome = ({ eventId, onPrompt, disabled }: AssistantHomeProps) => {
  const { t } = useTranslation();
  const { loading, chips, actionInsights, onTrack } = useAssistantInsights(eventId);

  const visibleInsights = actionInsights.slice(0, MAX_VISIBLE_INSIGHTS);

  return (
    <Stack direction="column" spacing={3.5} sx={{ p: { xs: 2.5, md: 3 } }}>
      {/* Warm lead line — the assistant's greeting, or the "all clear" note. */}
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 500, color: 'text.secondary', lineHeight: 1.5 }}
      >
        {onTrack ? t('assistant.insights.on_track') : t('assistant.home.greeting')}
      </Typography>

      {/* Next steps — spaced, soft rows (the app's elevation1 + tinted-icon-
          circle idiom, cf. ChecklistItemCard) rather than a cramped list. */}
      {(loading || visibleInsights.length > 0) && (
        <Stack direction="column" spacing={1.25}>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>
            {t('assistant.home.next_steps_title')}
          </Typography>

          {loading
            ? Array.from({ length: 2 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={60} sx={{ borderRadius: 2 }} />
              ))
            : visibleInsights.map((insight) => {
                const urgent = insight.priority <= 1;
                const text = t(`assistant.insights.${insight.id}`, {
                  ...insight.params,
                  defaultValue: '',
                });
                if (!text) return null;
                return (
                  <Stack
                    key={insight.id}
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{ p: 1.5, borderRadius: 2, bgcolor: 'background.elevation1' }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        bgcolor: urgent ? 'error.lighter' : 'primary.lighter',
                        color: urgent ? 'error.main' : 'primary.main',
                      }}
                    >
                      <IconifyIcon
                        icon={
                          urgent
                            ? 'material-symbols:priority-high-rounded'
                            : 'material-symbols:arrow-circle-right-outline-rounded'
                        }
                        sx={{ fontSize: 20 }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.5 }}>
                      {text}
                    </Typography>
                  </Stack>
                );
              })}
        </Stack>
      )}

      {/* Try asking — suggestion chips. */}
      <Box>
        <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
          {t('assistant.home.suggestions_title')}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          {chips.map((chipId) => {
            const label = t(`assistant.chips.${chipId}`, { defaultValue: '' });
            if (!label) return null;
            return (
              <Chip
                key={chipId}
                label={label}
                variant="soft"
                color="primary"
                disabled={disabled}
                onClick={() => onPrompt(label)}
              />
            );
          })}
        </Stack>
      </Box>
    </Stack>
  );
};

export default AssistantHome;
